import { schemaFieldName } from '@/components/claims/newClaim/schema/schemaFieldName';
import {
  getNewClaimMultiStepFormSchema,
  getNoOfClaimSchemaSteps,
} from '@/components/claims/newClaim/schema/steps';
import { getNewClaimStepPaymentContactFormSchema } from '@/components/claims/newClaim/schema/steps/stepPaymentContact';
import type { User } from '@/store/authStore';
import type { ClaimFormStepValues, ClaimStepsStateProps } from '@/store/newClaim/claimStepsStore';
import {
  SchemaIncidentTiming,
  SchemaPolicyType,
  SchemaPreferredContactMethod,
  SchemaProviderAccountType,
  SchemaTravellersType,
  SchemaTripTypeCode,
  type AmountOfMoney,
  type DraftClaimAdditionalExpense,
  type DraftClaimCancellationExpense,
  type DraftClaimMedicalExpense,
  type DraftClaimPerson,
  type SchemaTravellersRelationship,
  type UserDraftClaim,
  type UserPolicyDetails,
  type UserPolicyTraveller,
} from '@/types';
import axios from 'axios';
import { isBefore } from 'date-fns';
import removeUndefinedObjects from 'remove-undefined-objects';
import sanitized from 'sanitized';
import {
  isBoolean,
  isBooleanString,
  isNullOrUndefined,
  isObject,
  isString,
  isUndefined,
} from './assertions';
import { generateCorrelationIdForBFF, generateUniqueIdentifierId, toTitleCase } from './common';
import {
  formatDateForForm,
  formatDateInTravellers,
  formatTimeForForm,
  parseDateForTravellers,
} from './date';
import { getFrontendBuild, getServiceAPIBaseURL } from './env';

type FormStepValues = NonNullable<ClaimStepsStateProps['formStep']>['values'];

interface CreateDraftClaimParams {
  draftClaim: UserDraftClaim;
  claimUser?: User;
}

interface CreateDraftClaimResponse {
  data?: UserDraftClaim;
  error?: Error;
}

type SaveDraftClaimParams = CreateDraftClaimParams;

type SaveDraftClaimResponse = CreateDraftClaimResponse;

type FetchDraftClaimResponse = CreateDraftClaimResponse;

type FetchDraftClaimParams = Pick<CreateDraftClaimParams, 'claimUser'> & {
  draftClaimId: string;
};

type SubmitDraftClaimParams = CreateDraftClaimParams;

interface SubmitDraftClaimResponseData {
  draftClaimId: string;
  claimId: string;
}

interface SubmitDraftClaimResponse {
  data?: SubmitDraftClaimResponseData;
  error?: Error;
}

type GetClaimIncidentTimingParams = Partial<
  Pick<UserPolicyDetails, 'policyStart'> &
    Pick<NonNullable<UserDraftClaim['incident']>, 'incidentDateTime'>
>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UnflattenedFormStepValues = Record<string, any> & {
  policyType?: SchemaPolicyType;
  tripDetails?: {
    impactedTravellers?: {
      listedTravellers?: string | string[];
      unlistedTravellers?: Record<
        string,
        {
          lastName: string;
          firstName: string;
          relationshipToPolicyHolder: SchemaTravellersRelationship;
        }
      >;
    };
  };
  incident?: {
    incidentDate?: string;
    incidentTime?: string;
    injuryDetails?: {
      vehicleSpecificInjuryDetails?: {
        otherInvolvedPersons?: Record<string, DraftClaimPerson>;
      };
    };
    hospitalisationDetails?: {
      admissionDate?: string;
      admissionTime?: string;
      dischargeDate?: string;
      dischargeTime?: string;
    };
  };
  expenses?: {
    hasMedicalExpense?: boolean;
    hasAdditionalExpense?: boolean;
    hasCancellationExpense?: boolean;
    medicalExpenses?: Record<string, DraftClaimMedicalExpense>;
    additionalExpenses?: Record<string, DraftClaimAdditionalExpense>;
    cancellationExpenses?: Record<string, DraftClaimCancellationExpense>;
  };
  notifications?: {
    mobileNumber?: string;
  };
};

// Introduction is 'step 0', and choosing the claim type is 'step 1'.
function getClaimIntroStep() {
  return 0;
}

function getClaimEventStep() {
  return 1;
}

function getClaimFormStepStart() {
  return 2;
}

function getClaimUploadsStep() {
  return getClaimFormStepStart() + getNoOfClaimSchemaSteps();
}

function getClaimPaymentStep() {
  return getClaimUploadsStep() + 1;
}

function getClaimReviewStep() {
  return getClaimPaymentStep() + 1;
}

function getClaimSubmittedStep() {
  return getClaimReviewStep() + 1;
}

function getClaimFinalStep() {
  return getClaimSubmittedStep();
}

function getClaimTotalSteps() {
  return getClaimReviewStep();
}

function getIsClaimIntroStep(currentStep: number) {
  return currentStep === getClaimIntroStep();
}

function getIsClaimEventStep(currentStep: number) {
  return currentStep === getClaimEventStep();
}

function getIsClaimUploadsStep(currentStep: number) {
  return currentStep === getClaimUploadsStep();
}

function getIsClaimPaymentStep(currentStep: number) {
  return currentStep === getClaimPaymentStep();
}

function getIsClaimReviewStep(currentStep: number) {
  return currentStep === getClaimReviewStep();
}

function getIsClaimSubmittedStep(currentStep: number) {
  return currentStep === getClaimSubmittedStep();
}

function getIsClaimFormStep(currentStep: number) {
  return currentStep >= getClaimFormStepStart() && currentStep < getClaimUploadsStep();
}

function getClaimIncidentTiming({
  policyStart,
  incidentDateTime,
}: GetClaimIncidentTimingParams): SchemaIncidentTiming {
  if (isNullOrUndefined(incidentDateTime) || isNullOrUndefined(policyStart)) {
    return SchemaIncidentTiming.OnTrip;
  }

  if (isBefore(incidentDateTime, policyStart)) {
    return SchemaIncidentTiming.PreTrip;
  }

  return SchemaIncidentTiming.OnTrip;
}

function flattenTravellersForForm(travellers?: UserPolicyTraveller[]) {
  try {
    return travellers?.map(({ firstName, lastName, dateOfBirth }) => {
      const nameFormattedForValue =
        // Traveller name is converted back to be title-cased with spaces, before it is sent to the BFF.
        `${firstName.trim().replaceAll(' ', '_')} ${lastName.trim().replaceAll(' ', '_')}`.toLowerCase();
      const nameFormattedForLabel = toTitleCase(
        `${firstName.trim().replaceAll('_', ' ')} ${lastName.trim().replaceAll('_', ' ')}`,
      );
      const dobFormatted = `(${formatDateInTravellers(dateOfBirth)})`;
      const value = `${nameFormattedForValue} ${dobFormatted}`;
      const label = `${nameFormattedForLabel} ${dobFormatted}`;
      return { label, value };
    });
  } catch (error) {
    console.error(error);
    return undefined;
  }
}

function unflattenTravellersInForm(listedTravellers?: string | string[]) {
  try {
    if (!listedTravellers) {
      return undefined;
    }

    let travellers = listedTravellers;
    if (!Array.isArray(travellers)) {
      travellers = [travellers];
    }

    return travellers.map(traveller => {
      const values = traveller.split(' ');
      return {
        firstName: toTitleCase(values[0].trim().replaceAll('_', ' ')),
        lastName: toTitleCase(values[1].trim().replaceAll('_', ' ')),
        dateOfBirth: parseDateForTravellers(
          values[2].replace('(', '').replace(')', ''),
        ).toISOString(),
      } as UserPolicyTraveller;
    });
  } catch (error) {
    console.error(error);
    return undefined;
  }
}

/* eslint-disable */
// Disabling 'eslint' to use 'any', unsafe 'assign' and 'return'.
function flattenDraftClaimForForm(draftClaim: UserDraftClaim) {
  const { tripDetails, incident, expenses } = schemaFieldName;
  const { listedTravellers, unlistedTravellers } = tripDetails.impactedTravellers;
  const { otherInvolvedPersons } = incident.injuryDetails.vehicleSpecificInjuryDetails;
  const { medicalExpenses, additionalExpenses, cancellationExpenses } = draftClaim.expenses ?? {};

  const clonedDraftClaim = structuredClone(draftClaim);
  if (!isNullOrUndefined(clonedDraftClaim.expenses)) {
    if (medicalExpenses) {
      clonedDraftClaim.expenses.hasMedicalExpense = !!medicalExpenses.length; // Only used internally.
    }

    if (additionalExpenses) {
      clonedDraftClaim.expenses.hasAdditionalExpense = !!additionalExpenses.length; // Only used internally.
    }

    if (cancellationExpenses) {
      clonedDraftClaim.expenses.hasCancellationExpense = !!cancellationExpenses.length; // Only used internally.
    }
  }

  // These are a part of the draft claim, and don't come from the form fields.
  const keysToIgnore = ['draftClaimId', 'createdDateTime', 'updatedDateTime'];

  // Internal function to recursively flatten the given 'draftClaim' object.
  const flattenObject = (obj: Record<string, any>, prefix = '') => {
    const flattenedResult: Record<string, any> = {};
    const cancellationExpenseIds: string[] = [];
    const additionalExpenseIds: string[] = [];
    const medicalExpenseIds: string[] = [];

    Object.entries(obj).forEach(([key, value]) => {
      const newKey = prefix ? `${prefix}_${key}` : key;
      if (keysToIgnore.includes(newKey)) {
        return;
      }

      // Form fields cannot be 'null', but can be 'undefined' (if needed).
      if (isNullOrUndefined(value)) {
        return;
      }

      // NOTE: 'boolean' values need to be transformed into 'true', 'false'.
      if (isBoolean(value)) {
        flattenedResult[newKey] = value === true ? 'true' : 'false';
        return;
      }

      // NOTE: Date values have to be transformed from their 'ISO' format.
      if (newKey.includes('DateTime')) {
        const dateKey = newKey.replace('DateTime', 'Date');
        const timeKey = newKey.replace('DateTime', 'Time');
        flattenedResult[dateKey] = formatDateForForm(value);
        flattenedResult[timeKey] = formatTimeForForm(value);
        return;
      }

      if (newKey.includes('Date')) {
        flattenedResult[newKey] = formatDateForForm(value);
        return;
      }

      try {
        // NOTE: 'listedTravellers' needs to be transformed from an array.
        if (newKey === listedTravellers) {
          if (!value.length) {
            return;
          }

          flattenedResult[tripDetails.travellersType] = SchemaTravellersType.Listed;
          const travellers = flattenTravellersForForm(value)?.map(({ value }) => value);
          flattenedResult[newKey] = travellers;
          return;
        }

        // NOTE: 'unlistedTravellers' needs to be transformed from an array.
        if (newKey.includes('unlistedTravellers')) {
          if (!value.length) {
            return;
          }

          flattenedResult[tripDetails.travellersType] = SchemaTravellersType.NotListed;
          flattenedResult[unlistedTravellers.firstName] = value[0].firstName;
          flattenedResult[unlistedTravellers.lastName] = value[0].lastName;
          flattenedResult[unlistedTravellers.relationshipToPolicyHolder] =
            value[0].relationshipToPolicyHolder;
          return;
        }

        // NOTE: 'otherInvolvedPersons' needs to be transformed from an array.
        if (newKey.includes('otherInvolvedPersons')) {
          if (!value.length) {
            return;
          }

          flattenedResult[otherInvolvedPersons.firstName] = value[0].firstName;
          flattenedResult[otherInvolvedPersons.lastName] = value[0].lastName;
          return;
        }

        // NOTE: 'medicalExpenses' needs to be transformed from an array.
        const isMedicalExpenses = newKey.includes('medicalExpenses');
        if (isMedicalExpenses) {
          if (!value.length) {
            return;
          }

          const newValue = flattenObject(
            [...value].reduce((acc, currValue, index) => ({ ...acc, [index]: currValue }), {}),
          );

          let accountType: SchemaProviderAccountType | undefined;
          Object.entries(newValue).forEach(([itemKey, itemValue]) => {
            const flatKey = `${newKey}_${itemKey}`;
            flattenedResult[flatKey] = itemValue;
            if (itemKey.includes('accountType')) {
              accountType = itemValue;
              return;
            }

            if (itemKey.includes('expenseId')) {
              medicalExpenseIds.push(itemValue);
            }
          });

          Object.entries(flattenedResult).forEach(([flatKey, flatValue]) => {
            if (flatKey.includes('accountNumber')) {
              // 'swiftCode' needs to be extracted from 'accountType' and 'accountNumber'.
              if (accountType === SchemaProviderAccountType.SWIFT) {
                flattenedResult[flatKey.replace('accountNumber', 'swiftCode')] = flatValue;
                return;
              }

              // 'ibanNumber' needs to be extracted from 'accountType' and 'accountNumber'.
              if (accountType === SchemaProviderAccountType.IBAN) {
                flattenedResult[flatKey.replace('accountNumber', 'ibanNumber')] = flatValue;
              }
            }
          });
        }

        // NOTE: 'additionalExpenses', 'cancellationExpenses' need to be transformed from an array.
        const isAdditionalExpenses = newKey.includes('additionalExpenses');
        const isCancellationExpenses = newKey.includes('cancellationExpenses');
        if (isAdditionalExpenses || isCancellationExpenses) {
          if (!value.length) {
            return;
          }

          const newValue = flattenObject(
            [...value].reduce((acc, currValue, index) => ({ ...acc, [index]: currValue }), {}),
          );

          Object.entries(newValue).forEach(([itemKey, itemValue]) => {
            const flatKey = `${newKey}_${itemKey}`;
            flattenedResult[flatKey] = itemValue;
            if (itemKey.includes('expenseId')) {
              if (isAdditionalExpenses) {
                additionalExpenseIds.push(itemValue);
                return;
              }

              if (isCancellationExpenses) {
                cancellationExpenseIds.push(itemValue);
              }
            }
          });
        }
      } catch (error) {
        console.error(error);
      }

      // If 'value' is an 'object', then recursively flatten the object.
      if (value && isObject(value) && !Array.isArray(value)) {
        Object.assign(flattenedResult, flattenObject(value, newKey));
        return;
      }

      // Ignore other primitive values/arrays, and assign them directly.
      flattenedResult[newKey] = value;
    });

    if (medicalExpenseIds.length) {
      flattenedResult[expenses.medicalExpenseIds] = medicalExpenseIds;
    }

    if (additionalExpenseIds.length) {
      flattenedResult[expenses.additionalExpenseIds] = additionalExpenseIds;
    }

    if (cancellationExpenseIds.length) {
      flattenedResult[expenses.cancellationExpenseIds] = cancellationExpenseIds;
    }

    return flattenedResult;
  };

  const flattenedDraftClaim = flattenObject(clonedDraftClaim);
  const multiStepFormSchemaWithPayment = [
    ...getNewClaimMultiStepFormSchema(),
    getNewClaimStepPaymentContactFormSchema(),
  ];

  const formStepValues: ClaimFormStepValues = {};
  multiStepFormSchemaWithPayment.forEach(({ name: stepName, form }) => {
    form.fields.forEach(({ name: fieldName }) => {
      const fieldValue = flattenedDraftClaim[fieldName];

      if (!isNullOrUndefined(fieldValue)) {
        if (!formStepValues[stepName]) {
          formStepValues[stepName] = {};
        }

        formStepValues[stepName][fieldName] = fieldValue;
      }
    });
  });

  return formStepValues;
}

function unflattenFormStepValues(stepValues?: FormStepValues) {
  if (!stepValues) {
    return {};
  }

  const flattenedValues = Object.values(stepValues).reduce(
    (acc, value) => ({ ...acc, ...value }),
    {},
  );

  const unflattenedValues: UnflattenedFormStepValues = {};
  for (const key of Object.keys(flattenedValues)) {
    const keyParts = key.split('_');
    keyParts.reduce((acc, part, index) => {
      if (index === keyParts.length - 1) {
        const value = flattenedValues[key];
        // NOTE: 'true', 'false' values need to be transformed into 'boolean'.
        if (isBooleanString(value)) {
          acc[part] = value === 'true';
          return;
        }

        // NOTE: Date values have to be transformed into their 'ISO' format.
        if (isString(value) && part.toLowerCase().includes('date')) {
          acc[part] = new Date(value).toISOString();
          return;
        }

        acc[part] = value;
      } else {
        acc[part] = acc[part] || {};
      }
      return acc[part];
    }, unflattenedValues);
  }

  // Return unflattened step values after cleaning/sanitization.
  return sanitized(removeUndefinedObjects(unflattenedValues));
}

/* eslint-enable */
// Map and filter 'expenseId' values, so they can be used for tracking.
function getExpenseIdsInExpenses(expenses?: { expenseId?: string }[]) {
  const expenseIds = expenses
    ?.map(({ expenseId }) => expenseId)
    .filter(expenseId => !isNullOrUndefined(expenseId));
  if (!expenseIds?.length) {
    return undefined;
  }

  return expenseIds;
}

function generateDraftClaimFromStepValues(stepValues?: FormStepValues) {
  const unflattenedValues = unflattenFormStepValues(stepValues) ?? {};
  const draftClaim = structuredClone(unflattenedValues) as UserDraftClaim;

  const { policyType, tripDetails, incident, expenses, notifications } = unflattenedValues ?? {};
  const { incidentDate, incidentTime, injuryDetails, hospitalisationDetails } = incident ?? {};
  const { medicalExpenses, cancellationExpenses, additionalExpenses } = expenses ?? {};

  const { unlistedTravellers, listedTravellers } = tripDetails?.impactedTravellers ?? {};
  const { otherInvolvedPersons } = injuryDetails?.vehicleSpecificInjuryDetails ?? {};
  const { admissionDate, admissionTime, dischargeDate, dischargeTime } =
    hospitalisationDetails ?? {};

  // NOTE: 'tripType needs to be derived from 'policyType'.
  if (policyType && draftClaim.tripDetails) {
    draftClaim.tripDetails.tripType =
      policyType === SchemaPolicyType.AnnualMultiTrip
        ? SchemaTripTypeCode.AnnualMultiTrip
        : SchemaTripTypeCode.SingleTrip;
  }

  // NOTE: 'listedTravellers' needs to be transformed into an array.
  if (listedTravellers && draftClaim.tripDetails?.impactedTravellers) {
    draftClaim.tripDetails.impactedTravellers.listedTravellers =
      unflattenTravellersInForm(listedTravellers);
  }

  // NOTE: 'unlistedTravellers' needs to be transformed into an array.
  if (unlistedTravellers && draftClaim.tripDetails?.impactedTravellers) {
    draftClaim.tripDetails.impactedTravellers.unlistedTravellers =
      Object.values(unlistedTravellers);
  }

  // NOTE: 'otherInvolvedPersons' needs to be transformed into an array.
  const { vehicleSpecificInjuryDetails } = draftClaim.incident?.injuryDetails ?? {};
  if (otherInvolvedPersons && vehicleSpecificInjuryDetails?.otherInvolvedPersons) {
    vehicleSpecificInjuryDetails.otherInvolvedPersons = Object.values(otherInvolvedPersons);
  }

  // @TODO: Date patterns are similar, refactor below snippets to not be repetitive.
  // NOTE: 'incidentDate', 'incidentTime' need to be transformed into a single field.
  if (incidentDate && incidentTime && draftClaim.incident) {
    try {
      const time = incidentTime.split(':');
      const date = new Date(incidentDate);
      date.setHours(Number.parseInt(time[0]));
      date.setMinutes(Number.parseInt(time[1]));
      draftClaim.incident.incidentDateTime = date.toISOString();
      const { incident } = draftClaim as UnflattenedFormStepValues;
      delete incident?.incidentDate;
      delete incident?.incidentTime;
    } catch (error) {
      console.error(error);
      draftClaim.incident.incidentDateTime = 'Invalid Date';
    }
  }

  // NOTE: 'admissionDate',  'admissionTime, need to be transformed into a single field.
  if (admissionDate && admissionTime && draftClaim.incident?.hospitalisationDetails) {
    try {
      const time = admissionTime.split(':');
      const date = new Date(admissionDate);
      date.setHours(Number.parseInt(time[0]));
      date.setMinutes(Number.parseInt(time[1]));
      draftClaim.incident.hospitalisationDetails.admissionDateTime = date.toISOString();
      const { incident } = draftClaim as UnflattenedFormStepValues;
      delete incident?.hospitalisationDetails?.admissionDate;
      delete incident?.hospitalisationDetails?.admissionTime;
    } catch (error) {
      console.error(error);
      draftClaim.incident.hospitalisationDetails.admissionDateTime = 'Invalid Date';
    }
  }

  // NOTE: 'dischargeDate',  'dischargeTime, need to be transformed into a single field.
  if (dischargeDate && dischargeTime && draftClaim.incident?.hospitalisationDetails) {
    try {
      const time = dischargeTime.split(':');
      const date = new Date(dischargeDate);
      date.setHours(Number.parseInt(time[0]));
      date.setMinutes(Number.parseInt(time[1]));
      draftClaim.incident.hospitalisationDetails.dischargeDateTime = date.toISOString();
      const { incident } = draftClaim as UnflattenedFormStepValues;
      delete incident?.hospitalisationDetails?.dischargeDate;
      delete incident?.hospitalisationDetails?.dischargeTime;
    } catch (error) {
      console.error(error);
      draftClaim.incident.hospitalisationDetails.dischargeDateTime = 'Invalid Date';
    }
  }

  // NOTE: 'medicalExpenses' needs to be transformed into an array.
  if (medicalExpenses && draftClaim.expenses?.medicalExpenses) {
    try {
      draftClaim.expenses.medicalExpenses = Object.values(medicalExpenses).map(
        ({
          expenseId,
          expenseAmount,
          providerDetails,
          providerOwedAmount,
          ...otherExpenseDetails
        }) => {
          // Amounts cannot be half filled, it needs both values.
          if (!expenseAmount?.amount || !expenseAmount.currencyCode) {
            expenseAmount = undefined;
          }

          if (!providerOwedAmount?.amount || !providerOwedAmount.currencyCode) {
            providerOwedAmount = undefined;
          }

          const hasMatchingCurrency =
            !isUndefined(expenseAmount?.currencyCode) &&
            !isUndefined(providerOwedAmount?.currencyCode) &&
            expenseAmount.currencyCode === providerOwedAmount.currencyCode;

          let claimableAmount = 0;
          if (hasMatchingCurrency && expenseAmount?.amount && providerOwedAmount?.amount) {
            claimableAmount = expenseAmount.amount - providerOwedAmount.amount;
          } else if (expenseAmount?.currencyCode && expenseAmount?.amount) {
            claimableAmount = expenseAmount.amount;
          }

          let claimAmount: AmountOfMoney | undefined;
          if (expenseAmount?.currencyCode) {
            claimAmount = {
              amount: claimableAmount > 0 ? Number(claimableAmount.toFixed(2)) : 0,
              currencyCode: expenseAmount.currencyCode,
            };
          }

          // NOTE: 'accountNumber' is derived from 'swiftCode' and 'ibanNumber'.
          // NOTE: 'accountType' is derived from 'swiftCode' and 'ibanNumber'.
          const { swiftCode, ibanNumber } = providerDetails ?? {};
          if (swiftCode) {
            providerDetails = {
              ...providerDetails,
              accountNumber: swiftCode,
              accountType: SchemaProviderAccountType.SWIFT,
            };
          } else if (ibanNumber) {
            providerDetails = {
              ...providerDetails,
              accountNumber: ibanNumber,
              accountType: SchemaProviderAccountType.IBAN,
            };
          }

          return {
            ...otherExpenseDetails,
            expenseId: expenseId ?? generateUniqueIdentifierId(),
            claimAmount,
            expenseAmount,
            providerDetails,
            providerOwedAmount,
          };
        },
      );
    } catch (error) {
      console.error(error);
    }
  }

  // NOTE: 'cancellationExpenses' needs to be transformed into an array.
  if (cancellationExpenses && draftClaim.expenses?.cancellationExpenses) {
    try {
      draftClaim.expenses.cancellationExpenses = Object.values(cancellationExpenses).map(
        ({
          expenseId,
          claimAmount,
          expenseAmount,
          creditNoteOrRefundAmount,
          ...otherExpenseDetails
        }) => {
          // Amounts cannot be half filled, it needs both values.
          if (!claimAmount?.amount || !claimAmount.currencyCode) {
            claimAmount = undefined;
          }

          if (!expenseAmount?.amount || !expenseAmount.currencyCode) {
            expenseAmount = undefined;
          }

          if (!creditNoteOrRefundAmount?.amount || !creditNoteOrRefundAmount.currencyCode) {
            creditNoteOrRefundAmount = undefined;
          }

          return {
            ...otherExpenseDetails,
            expenseId: expenseId ?? generateUniqueIdentifierId(),
            claimAmount,
            expenseAmount,
            creditNoteOrRefundAmount,
          };
        },
      );
    } catch (error) {
      console.error(error);
    }
  }

  // NOTE: 'additionalExpenses' needs to be transformed into an array.
  if (additionalExpenses && draftClaim.expenses?.additionalExpenses) {
    try {
      draftClaim.expenses.additionalExpenses = Object.values(additionalExpenses).map(
        ({
          expenseId,
          claimAmount,
          expenseAmount,
          creditNoteOrRefundAmount,
          ...otherExpenseDetails
        }) => {
          // Amounts cannot be half filled, it needs both values.
          if (!claimAmount?.amount || !claimAmount.currencyCode) {
            claimAmount = undefined;
          }

          if (!expenseAmount?.amount || !expenseAmount.currencyCode) {
            expenseAmount = undefined;
          }

          if (!creditNoteOrRefundAmount?.amount || !creditNoteOrRefundAmount.currencyCode) {
            creditNoteOrRefundAmount = undefined;
          }

          return {
            ...otherExpenseDetails,
            expenseId: expenseId ?? generateUniqueIdentifierId(),
            claimAmount,
            expenseAmount,
            creditNoteOrRefundAmount,
          };
        },
      );
    } catch (error) {
      console.error(error);
    }
  }

  // NOTE: Providing 'mobileNumber' sets 'preferredContactMethod' to 'phone'.
  // NOTE: Providing 'mobileNumber' sets 'receiveClaimNotifications' to 'true'.
  if (draftClaim.notifications) {
    const { emailAddress } = draftClaim.notifications;
    const { mobileNumber } = notifications ?? {};
    const hasMobileNumber = !!mobileNumber;

    if (hasMobileNumber) {
      draftClaim.notifications = {
        emailAddress,
        mobileNumber,
        preferredContactMethod: SchemaPreferredContactMethod.Phone,
        receiveClaimNotifications: true,
      };
    } else {
      draftClaim.notifications = {
        emailAddress,
        preferredContactMethod: SchemaPreferredContactMethod.Email,
        receiveClaimNotifications: false,
      };
    }
  }

  // @TODO: Need to add transformations for remaining fields.
  draftClaim.draftClaimStatus = 'draft';
  return draftClaim;
}

let fetchDraftClaimController: AbortController | null = null;
async function fetchDraftClaim({
  draftClaimId,
  claimUser,
}: FetchDraftClaimParams): Promise<FetchDraftClaimResponse> {
  const url = `${getServiceAPIBaseURL()}/draft-claims/${draftClaimId}`;

  if (!claimUser) {
    return { error: new Error('Invalid claim user.') };
  }

  if (!draftClaimId) {
    return { error: new Error('Invalid claim number.') };
  }

  if (fetchDraftClaimController) {
    fetchDraftClaimController.abort();
    fetchDraftClaimController = null;
  }

  try {
    fetchDraftClaimController = new AbortController();
    const response = await axios.get<UserDraftClaim>(url, {
      signal: fetchDraftClaimController.signal,
      withCredentials: true,
      headers: {
        'x-correlation-id': generateCorrelationIdForBFF(),
        x_cp_fe_build: getFrontendBuild(),
        'content-type': 'application/json',
        'x-user-id': claimUser.email,
        'x-csrf': '1',
      },
    });

    const { data } = response;
    if (!data || data.draftClaimId !== draftClaimId) {
      return { error: new Error('Error fetching draft claim.') };
    }

    const updatedData = { ...data };
    const { expenses } = updatedData;
    if (!isNullOrUndefined(expenses)) {
      if (expenses.medicalExpenses) {
        updatedData.expenses = {
          ...updatedData.expenses,
          hasMedicalExpense: !!expenses.medicalExpenses.length,
          medicalExpenseIds: getExpenseIdsInExpenses(expenses.medicalExpenses),
        };
      }

      if (expenses.additionalExpenses) {
        updatedData.expenses = {
          ...updatedData.expenses,
          hasAdditionalExpense: !!expenses.additionalExpenses.length,
          additionalExpenseIds: getExpenseIdsInExpenses(expenses.additionalExpenses),
        };
      }

      if (expenses.cancellationExpenses) {
        updatedData.expenses = {
          ...updatedData.expenses,
          hasCancellationExpense: !!expenses.cancellationExpenses.length,
          cancellationExpenseIds: getExpenseIdsInExpenses(expenses.cancellationExpenses),
        };
      }
    }

    return { data: updatedData };
  } catch (error) {
    console.error(error);
    return { error: error as Error };
  }
}

let createDraftClaimController: AbortController | null = null;
async function createDraftClaim({
  draftClaim,
  claimUser,
}: CreateDraftClaimParams): Promise<CreateDraftClaimResponse> {
  const url = `${getServiceAPIBaseURL()}/draft-claims`;

  if (!claimUser) {
    return { error: new Error('Invalid claim user.') };
  }

  if (!draftClaim.policyNumber) {
    return { error: new Error('Invalid policy number.') };
  }

  if (!draftClaim.policyType) {
    return { error: new Error('Invalid policy type.') };
  }

  if (createDraftClaimController) {
    createDraftClaimController.abort();
    createDraftClaimController = null;
  }

  try {
    createDraftClaimController = new AbortController();
    const { policyType, draftClaimStatus, ...otherValues } = draftClaim;
    const response = await axios.post<UserDraftClaim>(url, otherValues, {
      signal: createDraftClaimController.signal,
      withCredentials: true,
      headers: {
        'x-correlation-id': generateCorrelationIdForBFF(),
        x_cp_fe_build: getFrontendBuild(),
        'content-type': 'application/json',
        'x-user-id': claimUser.email,
        'x-csrf': '1',
      },
    });

    const { data } = response;
    if (!data || data.policyNumber !== draftClaim.policyNumber || !data.draftClaimId) {
      return { error: new Error('Error creating draft claim.') };
    }

    return { data: { ...data, policyType, draftClaimStatus } };
  } catch (error) {
    console.error(error);
    return { error: error as Error };
  }
}

let saveDraftClaimController: AbortController | null = null;
async function saveDraftClaim({
  draftClaim,
  claimUser,
}: SaveDraftClaimParams): Promise<SaveDraftClaimResponse> {
  const url = `${getServiceAPIBaseURL()}/draft-claims/${draftClaim.draftClaimId}`;

  if (!claimUser) {
    return { error: new Error('Invalid claim user.') };
  }

  if (!draftClaim.draftClaimId) {
    return { error: new Error('Invalid claim number.') };
  }

  if (saveDraftClaimController) {
    saveDraftClaimController.abort();
    saveDraftClaimController = null;
  }

  try {
    saveDraftClaimController = new AbortController();
    const response = await axios.put<UserDraftClaim>(url, draftClaim, {
      signal: saveDraftClaimController.signal,
      withCredentials: true,
      headers: {
        'x-correlation-id': generateCorrelationIdForBFF(),
        x_cp_fe_build: getFrontendBuild(),
        'content-type': 'application/json',
        'x-user-id': claimUser.email,
        'x-csrf': '1',
      },
    });

    const { data } = response;
    if (!data || data.policyNumber !== draftClaim.policyNumber) {
      return { error: new Error('Error saving draft claim.') };
    }

    const updatedData = { ...data };
    const { expenses } = updatedData;
    if (!isNullOrUndefined(expenses)) {
      if (isBoolean(draftClaim?.expenses?.hasMedicalExpense)) {
        updatedData.expenses = {
          ...updatedData.expenses,
          hasMedicalExpense: draftClaim.expenses.hasMedicalExpense,
          medicalExpenseIds: getExpenseIdsInExpenses(expenses.medicalExpenses),
        };
      }

      if (isBoolean(draftClaim?.expenses?.hasAdditionalExpense)) {
        updatedData.expenses = {
          ...updatedData.expenses,
          hasAdditionalExpense: draftClaim.expenses.hasAdditionalExpense,
          additionalExpenseIds: getExpenseIdsInExpenses(expenses.additionalExpenses),
        };
      }

      if (isBoolean(draftClaim?.expenses?.hasCancellationExpense)) {
        updatedData.expenses = {
          ...updatedData.expenses,
          hasCancellationExpense: draftClaim.expenses.hasCancellationExpense,
          cancellationExpenseIds: getExpenseIdsInExpenses(expenses.cancellationExpenses),
        };
      }
    }

    return { data: updatedData };
  } catch (error) {
    console.error(error);
    return { error: error as Error };
  }
}

let submitDraftClaimController: AbortController | null = null;
async function submitDraftClaim({
  draftClaim,
  claimUser,
}: SubmitDraftClaimParams): Promise<SubmitDraftClaimResponse> {
  const url = `${getServiceAPIBaseURL()}/draft-claims/${draftClaim.draftClaimId}/claims`;

  if (!claimUser) {
    return { error: new Error('Invalid claim user.') };
  }

  if (!draftClaim.draftClaimId) {
    return { error: new Error('Invalid claim number.') };
  }

  if (submitDraftClaimController) {
    submitDraftClaimController.abort();
    submitDraftClaimController = null;
  }

  try {
    submitDraftClaimController = new AbortController();
    const response = await axios.post<SubmitDraftClaimResponseData>(url, undefined, {
      signal: submitDraftClaimController.signal,
      withCredentials: true,
      headers: {
        'x-correlation-id': generateCorrelationIdForBFF(),
        x_cp_fe_build: getFrontendBuild(),
        'content-type': 'application/json',
        'x-user-id': claimUser.email,
        'x-csrf': '1',
      },
    });

    const { data } = response;
    const { draftClaimId } = draftClaim;
    if (!data || !data.claimId || data.draftClaimId !== draftClaimId) {
      return { error: new Error('Error submitting draft claim.') };
    }

    return { data };
  } catch (error) {
    console.error(error);
    return { error: error as Error };
  }
}

export {
  createDraftClaim,
  fetchDraftClaim,
  flattenDraftClaimForForm,
  flattenTravellersForForm,
  generateDraftClaimFromStepValues,
  getClaimEventStep,
  getClaimFinalStep,
  getClaimFormStepStart,
  getClaimIncidentTiming,
  getClaimIntroStep,
  getClaimPaymentStep,
  getClaimReviewStep,
  getClaimSubmittedStep,
  getClaimTotalSteps,
  getClaimUploadsStep,
  getIsClaimEventStep,
  getIsClaimFormStep,
  getIsClaimIntroStep,
  getIsClaimPaymentStep,
  getIsClaimReviewStep,
  getIsClaimSubmittedStep,
  getIsClaimUploadsStep,
  saveDraftClaim,
  submitDraftClaim,
};
